import { UI, HStack, Stack, VStack } from '@genial/components';
import { BoxRadius, Typography, BoxBorder } from '@genial/design-tokens';
import { Pressable } from 'native-base';
import React from 'react';

import { InputMoney } from '../../../../../../../../../components/InputMoney';
import {
  TableContent,
  TableContentItemCaptions,
  TableContentItemText,
} from '../../../../../../../../../components/TableContent';
import { useGlobalAssetContext } from '../../../../../contexts';
import { useFixedIncomeAssetContext } from '../../../../../contexts/products/FixedIncome';
import { useFixedIncomeAssetController } from '../../../../../hooks/products/FixedIncome';
import { LateralBar } from '../../../components/LateralBar';
import { ListLoadingItemMobile } from '../ListLoadingItem/mobile';
import {
  ApplicationInfo,
  FeeInfo,
  MaturityInfo,
  NetValueInfo,
  ProfitabilityInfo,
  RiskInfo,
  TaxInfo,
  UnitPriceInfo,
} from '../shared';

import { FixedIncomeListItemTypes } from '.';

export const FixedIncomeListItemMobile: React.FC<FixedIncomeListItemTypes.Props> = ({ offer }) => {
  const id = offer.productIdNormalized;
  const {
    control,
    currentValue,
    handleInputBlur,
    handleInputClear,
    handleInputFocus,
    handleSubmit,
    hasError,
    helpText,
    helpTextProps,
    isFocused,
    showMessageAdjustUnitPrice,
    currentValueFormatted,
    ableToChangeFees,
    selectedFee,
    selectedFeeId,
    setSelectedFeeId,
    minApplication,
    estimatedIncomeCalculated,
    estimatedNetValueCalculated,
  } = useFixedIncomeAssetController(offer);
  const { setModalOpen, setOfferSelected, setOfferFeeSelected } = useFixedIncomeAssetContext();
  const { infoGetter, selectedTab } = useGlobalAssetContext();

  const completeOffer = {
    ...offer,
    orderValue: currentValue || 0,
    orderFee: selectedFee,
    estimatedIncomeCalculated,
    estimatedNetValueCalculated,
  };

  const { Button, isSelected, forceLoading } = infoGetter(
    'fixedIncome',
    completeOffer,
    'productIdNormalized'
  );

  const onOpenDetailsModal = () => {
    setModalOpen(true);
    setOfferSelected(offer);
    setOfferFeeSelected(selectedFee);
  };

  if (forceLoading) {
    return <ListLoadingItemMobile />;
  }

  const infosProps = {
    offer: completeOffer,
    selectedFee,
    estimatedIncomeCalculated,
    estimatedNetValueCalculated,
  };

  return (
    <TableContent direction="row" space={0} p={0} px="inline.xxxs" isHighlighted={isSelected}>
      <LateralBar
        backgroundColor={selectedTab.color}
        borderTopLeftRadius={BoxRadius.sm}
        borderBottomLeftRadius={BoxRadius.sm}
      />
      <VStack padding="inset.sm" space="stack.xxxs" w="full">
        <HStack justifyContent="space-between" alignItems="center">
          <UI.Text variant="semibold" fontSize={Typography['fontsize.lg']}>
            {offer.productDisplayNormalized}
          </UI.Text>
          <Pressable onPress={onOpenDetailsModal}>
            <UI.Icon name="Info" size="xs" />
          </Pressable>
        </HStack>
        <HStack alignItems="center" justifyContent="space-between">
          <Stack w="45%">
            <TableContentItemCaptions icons={offer.captions} />
          </Stack>
          <RiskInfo w="45%" {...infosProps} />
        </HStack>
        <HStack justifyContent="space-between">
          <ApplicationInfo w="45%" {...infosProps} />
          <TaxInfo w="45%" {...infosProps} />
        </HStack>
        <HStack justifyContent="space-between">
          <MaturityInfo w="45%" {...infosProps} />
          <UnitPriceInfo w="45%" {...infosProps} />
        </HStack>
        <HStack justifyContent="space-between">
          <Stack w="45%">
            <HStack space="inline.quarck" alignItems="center">
              <UI.Text>Taxa</UI.Text>
              <UI.Text fontSize="xs">(Spread)</UI.Text>
            </HStack>
            <Stack bgColor="backgroundshade" borderRadius={BoxBorder.thick}>
              <UI.Select
                triggerProps={{
                  isDisabled: !ableToChangeFees,
                  _disabled: { opacity: 0.2 },
                }}
                testID="fee-select"
                options={offer.distributionFeesNormalized.map((feeOption) => ({
                  label: feeOption.label,
                  value: feeOption.feeId.toString(),
                }))}
                disabled={isSelected}
                placeholder="--"
                selectedValue={selectedFeeId?.toString() || ''}
                onValueChange={(value) => setSelectedFeeId(Number(value))}
              />
            </Stack>
          </Stack>
          <ProfitabilityInfo w="45%" {...infosProps} />
        </HStack>
        <HStack justifyContent="space-between">
          <FeeInfo w="45%" {...infosProps} />
          <NetValueInfo w="45%" {...infosProps} />
        </HStack>
        <HStack space="inline.xxxs" justifyContent="space-between" w="full">
          <VStack space="stack.quarck" w="75%">
            <UI.Text>Valor a aplicar:</UI.Text>
            <VStack flex={1}>
              <InputMoney
                id={`fixedIncome-input${id}`}
                name="createAppliedAmount"
                control={control}
                handleFocus={handleInputFocus}
                handleBlur={handleInputBlur}
                handleSubmit={handleSubmit}
                handleClear={handleInputClear}
                isDisabled={isSelected}
                currentValue={currentValueFormatted}
                hasFocus={isFocused}
                minValue={minApplication}
                customInputProps={{
                  ...{
                    ...(showMessageAdjustUnitPrice && {
                      helpTextProps,
                      helpText,
                    }),
                  },
                  ...{ ...(isFocused && hasError && { error: hasError }) },
                  ...{ ...(isFocused && !hasError && { helpText }) },
                }}
              />
              {showMessageAdjustUnitPrice && !isFocused && (
                <TableContentItemText fontSize={Typography['fontsize.xs']} color="neutral.light">
                  Ajustado por preço unitário
                </TableContentItemText>
              )}
            </VStack>
          </VStack>
          <Stack alignSelf="flex-end">
            <Button
              id={selectedTab.typeGetterId}
              offer={completeOffer}
              isDisabled={typeof hasError?.message === 'string'}
              productContextConstant={selectedTab}
            />
          </Stack>
        </HStack>
      </VStack>
    </TableContent>
  );
};
